'use client';

// TODO fix jsx-a11y

/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import cn from 'classnames';
import React, { useState } from 'react';
import HTMLString from 'react-html-string';

import type { Locale } from '@shared/libs';
import { ButtonExpand, ButtonExpandType, H2, UnboundedFontH3 } from '@shared/ui';
import type { FaqItem } from '@shared/utils';

import { FaqOption } from './consts';
import styles from './faq.module.css';

type TProps = {
  locale: Locale;
  instruments?: string;
  arrangement?: string;
  faqItems?: FaqItem[];
  mt?: boolean;
};

const Faq = ({ locale, instruments, arrangement, faqItems, mt }: TProps) => {
  // Helper функция для обработки переносов строк
  const formatDescription = (text: string): string => {
    if (!text) return '';
    // Заменяем переносы строк на <br> теги для корректного отображения
    return text.replace(/\n/g, '<br>');
  };

  // Используем переданные с сервера FAQ items, если они есть
  const dynamicFaqItems = faqItems || [];
  
  // Создаем состояние для FAQ опций на основе динамических данных
  const [faqOptionsMap, setFaqOptionsMap] = useState<FaqOption[]>(() => {
    // Если есть динамические FAQ, используем их
    if (dynamicFaqItems.length > 0) {
      return dynamicFaqItems.map((item, index) => ({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        id: item.id,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        title: item.question,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        description: formatDescription(item.answer),
        isExpanded: index === 0, // Первый элемент раскрыт по умолчанию
      }));
    }
    
    // Иначе используем стандартные FAQ (fallback)
    return [
      {
        id: 'faq_1',
        title: locale.NOTE_FAQ_Q1 || '',
        description: formatDescription(locale.NOTE_FAQ_A1 || ''),
        isExpanded: false,
      },
      {
        id: 'faq_2',
        title: locale.NOTE_FAQ_Q2 || '',
        description: formatDescription(locale.NOTE_FAQ_A2 || ''),
        isExpanded: false,
      },
      {
        id: 'faq_3',
        title: locale.NOTE_FAQ_Q3 || '',
        description: formatDescription(locale.NOTE_FAQ_A3 || ''),
        isExpanded: false,
      },
      {
        id: 'faq_4',
        title: locale.NOTE_FAQ_Q4 || '',
        description: formatDescription(locale.NOTE_FAQ_A4 || ''),
        isExpanded: false,
      },
      {
        id: 'faq_5',
        title: locale.NOTE_FAQ_Q5 || '',
        description: formatDescription(locale.NOTE_FAQ_A5 || ''),
        isExpanded: false,
      },
      {
        id: 'faq_6',
        title: locale.NOTE_FAQ_Q6 || '',
        description: `<table class="${styles.table}">
        <tr><td>${locale.NOTE_FAQ_A6_1_1}</td><td>${locale.NOTE_FAQ_A6_1_2}</td></tr>
        ${arrangement ? `<tr><td>${locale.NOTE_FAQ_A6_2_1}</td><td>${arrangement}</td></tr>` : ''}
        ${instruments ? `<tr><td>${locale.NOTE_FAQ_A6_3_1}</td><td>${instruments}</td></tr>` : ''} 
        <tr><td>${locale.NOTE_FAQ_A6_4_1}</td><td>${locale.NOTE_FAQ_A6_4_2}</td></tr>
        </table>`,
        isExpanded: true,
      },
    ];
  });

  const toggleAccordion = (event: React.MouseEvent) => {
    const targetId = event.currentTarget.id;
    if (!targetId) return;
    const newState = faqOptionsMap.map((option) =>
      option.id === targetId ? { ...option, isExpanded: !option.isExpanded } : option,
    );
    setFaqOptionsMap(newState);
  };

  // Не отображаем FAQ если нет переданных вопросов И нет fallback данных в локализации
  const hasFallbackData = locale.NOTE_FAQ_Q1 || locale.NOTE_FAQ_Q2 || locale.NOTE_FAQ_Q3 || locale.NOTE_FAQ_Q4 || locale.NOTE_FAQ_Q5 || locale.NOTE_FAQ_Q6;
  if ((!faqItems || faqItems.length === 0) && !hasFallbackData) {
    return null;
  }

  return (
    <div
      className={cn(styles.container, {
        [styles.mt]: mt,
      })}
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <H2 className={styles.h2} itemProp="name">
        {locale.NOTE_FAQ_TITLE}
      </H2>

      <div>
        {faqOptionsMap.map((option) => (
          <div
            key={option.id}
            className={styles.accordionItem}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
          >
            <div
              className={cn(styles.toggleControl, {
                [styles.expanded]: option.isExpanded,
              })}
              id={option.id}
              onClick={toggleAccordion}
            >
              <UnboundedFontH3 className={styles.accordionTitle} itemProp="name">
                {option.title}
              </UnboundedFontH3>
              <ButtonExpand
                type={option.isExpanded ? ButtonExpandType.CLOSE : ButtonExpandType.BLACK}
                className={styles.icon}
              />
            </div>
            <div
              className={styles.accordionContent}
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
            >
              <div className={styles.p} itemProp="text">
                <HTMLString html={option.description} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Faq;
